import { ClinicEducationalResource } from "@/types/clinic-types";

export interface CountryDefaults {
  /**
   * Hand-verified against that country's official health or government source.
   * No dataset can reliably answer "which number should a cardiac patient be
   * told to call": many countries have several emergency numbers, and the
   * wrong one reaches the wrong service. (India, for example, answers both 102
   * and 108 — 102 is maternal/child transport, 108 is the general emergency
   * ambulance.) Never copy one in from a library or a list.
   */
  emergencyNumber: string;
  /** Pre-filled educational link; countries without one start empty. */
  educationalResource?: ClinicEducationalResource;
}

/**
 * What the clinic form pre-fills for a country, per ISO 3166-1 alpha-2 code.
 *
 * Deliberately limited to the countries Vitall serves. Clinics elsewhere are
 * not blocked: the form leaves these fields empty and the OrgAdmin enters
 * them, which is the only step that puts a person who knows the locale behind
 * the values.
 *
 * Canada's educational resource is the link the patient app showed to every
 * patient before the field became configurable, so Canadian clinics keep what
 * they already had.
 *
 * To add a country: confirm its emergency number against an official source,
 * add the entry with a comment, and extend the tests in
 * `fhir-clinic-utils.test.ts`.
 */
export const COUNTRY_DEFAULTS: Record<string, CountryDefaults> = {
  AU: { emergencyNumber: "000" },
  CA: {
    emergencyNumber: "911",
    educationalResource: { label: "Heart Hub Website", url: "https://ourhearthub.ca/" },
  },
  GB: { emergencyNumber: "999" },
  US: { emergencyNumber: "911" },
};
